import { useSeoTags } from "../useSeoTags";
import "../marketMyths.css";
import "../humanArticleTemplate.css";

// WAVE 1, 2026-10-28 -- humanized rewrite of the Momentum-5d evidence doc.
// Isolated, unlinked, noindexed preview. Numbers re-verified fresh against
// feature_library (roc_5, mom_12_1) before writing.

const PER_SEGMENT: { label: string; value: number }[] = [
  { label: "Micro-cap", value: -0.0535 },
  { label: "Small-cap", value: -0.0435 },
  { label: "Mid-cap", value: -0.0385 },
  { label: "Large-cap", value: -0.0217 },
];

function SegmentIcChart() {
  const width = 640;
  const rowH = 40;
  const height = PER_SEGMENT.length * rowH + 20;
  const labelW = 100;
  const barAreaW = width - labelW - 70;
  const maxAbs = Math.max(...PER_SEGMENT.map((d) => Math.abs(d.value)));
  const scale = barAreaW / maxAbs;

  return (
    <figure className="hatpl-chart">
      <svg viewBox={`0 0 ${width} ${height}`} width="100%" role="img"
           aria-label="Correlation between 5-day rate of change and forward return, by segment: negative in every market-cap tier">
        {PER_SEGMENT.map((d, i) => {
          const y = 10 + i * rowH;
          const w = Math.abs(d.value) * scale;
          return (
            <g key={d.label}>
              <text x={0} y={y + 14} className="hatpl-chart-label">{d.label}</text>
              <rect x={labelW} y={y + 2} width={w} height={16} className="hatpl-chart-bar-neg" />
              <text x={labelW + w + 8} y={y + 14} className="hatpl-chart-value">
                {d.value.toFixed(4)}
              </text>
            </g>
          );
        })}
      </svg>
      <figcaption>
        Correlation between a stock's 5-day rate of change and its next return, measured separately in each
        market-cap segment. All four negative — the reversal isn't a quirk of one slice of the market.
      </figcaption>
      <div className="hatpl-asof">
        As of 2026&#8209;08&#8209;08 (blessing run) · re-validated on a slow cadence, not daily.
      </div>
    </figure>
  );
}

const CANONICAL = "https://tickerpicker.in/market-myths?evidence=momentum-5d";

export function Momentum5dArticlePage({ onBack }: { onBack: () => void }) {
  useSeoTags({
    title: "Chasing last week's winners is backwards — Tickerpicker",
    description:
      "Stocks that rose fastest over the past 5 days tend to give a little of it back next, not keep running " +
      "— a real, consistent reversal effect, measured across 2,468 trading days.",
    canonical: CANONICAL,
    ogType: "article",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: "Chasing last week's winners is backwards",
      url: CANONICAL,
      publisher: { "@type": "Organization", name: "Tickerpicker" },
      isPartOf: { "@type": "WebPage", name: "Market Myths: Tested", url: "https://tickerpicker.in/market-myths" },
    },
  });

  return (
    <div className="hatpl-shell">
      <nav className="hatpl-nav">
        <a href="/" className="hatpl-brand"><span className="dot" />TickerPicker</a>
        <div className="mm-evid-crumbs">
            <a href="/">Home</a>
            <span className="mm-crumb-sep">/</span>
            <button className="mm-back" onClick={onBack}>&larr; Back to Market Myths</button>
          </div>
      </nav>

      <article className="hatpl-article">
        <div className="hatpl-kicker">Market Myths: Tested — 5-day momentum</div>
        <h1>Chasing last week's winners is backwards</h1>

        <p>
          Buy what's hot. It's one of the oldest instincts in trading, and at a one-week horizon our own data
          says it's wrong — reliably, and in the opposite direction people expect.
        </p>

        <p>
          We measure a stock's rate of change over the past 5 trading days, then ask how it does next. Across
          2,468 trading days, the correlation comes out negative — about −0.037 — and the t-statistic, past 16,
          is one of the largest and most consistent we've measured anywhere in our feature library. Stocks that
          rose fastest over the last week tend to give a little of it back, not keep accelerating.
        </p>

        <SegmentIcChart />

        <p>
          It holds in every cap tier we checked, and it holds across different stretches of history rather than
          living in one regime. This is the textbook short-term reversal effect — fast, recent price moves
          partially unwind over the next few days, probably some mix of overreaction and short-term liquidity
          dynamics, not a trend continuing. There's a real wrinkle if you sort stocks into deciles by their 5-day
          move, though: monotonicity comes out at +0.48 — positive, even though the overall relationship is
          negative. It's also a much coarser read than the correlation above: ten bucket averages are a far
          noisier statistic than a rank correlation computed across every stock-date pair, which is where the
          actual statistical power sits — that's the t-statistic past 16, not this. The decile shape is a real,
          separate question on its own terms (and a different shape from the 52-week-high effect's own decile
          story elsewhere on this page); it doesn't undercut the headline finding.
        </p>

        <blockquote className="hatpl-pullquote">
          Statistically, one of the strongest things we've found. Practically, still a small effect.
        </blockquote>

        <p>
          A correlation of −0.037, however large its t-statistic, is a
          reliable way to rank stocks against each other — not a large, obviously tradeable mispricing on its
          own, and nothing here has been checked against trading costs.
        </p>

        <p>
          It's also worth not confusing this with momentum in the longer, academic sense — 6-to-12-month trends,
          which is a genuinely different and separately measured claim in our library. That longer feature runs
          <em> positive</em> on average, the opposite sign, which fits the textbook story that reversal and
          momentum operate at different horizons — but it's also flagged in our own records as unstable, its
          sign flipping across market regimes. It's mentioned here as context, not as support for this finding;
          the two have independent, separately-weak-or-strong track records.
        </p>

        <hr className="hatpl-rule" />

        <details className="hatpl-numbers">
          <summary>The full numbers</summary>
          <table>
            <tbody>
              <tr><td>Pooled correlation (IC)</td><td>&minus;0.0373</td></tr>
              <tr><td>t-statistic</td><td>&minus;16.2</td></tr>
              <tr><td>Trading dates</td><td>2,468</td></tr>
              <tr><td>Decile monotonicity</td><td>+0.4788</td></tr>
              <tr><td>Per-segment IC — micro-cap</td><td>&minus;0.0535</td></tr>
              <tr><td>Per-segment IC — small-cap</td><td>&minus;0.0435</td></tr>
              <tr><td>Per-segment IC — mid-cap</td><td>&minus;0.0385</td></tr>
              <tr><td>Per-segment IC — large-cap</td><td>&minus;0.0217</td></tr>
              <tr><td>Sub-period stability</td><td>stable sign across sub-periods</td></tr>
              <tr><td>Context — mom_12_1 (12-month) IC</td><td>+0.0320, t=11.48, flagged MARGINAL/UNSTABLE</td></tr>
              <tr><td>Blessed into production</td><td>2026-08-08</td></tr>
            </tbody>
          </table>
          <p className="hatpl-source">
            Source: <code>feature_library</code> (live production blessing record, <code>roc_5</code> and
            <code>mom_12_1</code>).
          </p>
        </details>

        <div className="mm-evid-footnav">
          <button className="mm-back" onClick={onBack}>&larr; Back to Market Myths</button>
        </div>
      </article>
    </div>
  );
}
